import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  Upload,
  message,
} from 'antd';
import {
  ApartmentOutlined,
  BgColorsOutlined,
  DeleteOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  PictureOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  SaveOutlined,
  ShoppingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { homepageConfigService, HomepageConfigItem } from '../services/homepageConfigService';
import { commonAttachFileService } from '../services/commonAttachFileService';
import { productGroupService } from '../services/productGroupService';
import type { ProductGroup } from '../types/productGroup';
import { useLanguage } from '../contexts/LanguageContext';
import type { MessageKey } from '../i18n';
import { translateAdminDom } from '../utils/domTranslator';

const { Option } = Select;

type CanvasMode = 'desktop' | 'mobile';
type ViewMode = 'visual' | 'tree';
type DropPosition = 'before' | 'after' | 'inside';

interface ModuleMeta {
  value: string;
  label: string;
  desc: string;
  icon: React.ReactNode;
  color: string;
  defaultName: string;
  defaultTitle?: string;
}

const buildModuleTypes = (t: (key: MessageKey, params?: Record<string, string | number>) => string): ModuleMeta[] => [
  {
    value: 'banner_block',
    label: t('hpModBannerBlockLabel'),
    desc: t('hpModBannerBlockDesc'),
    icon: <BgColorsOutlined />,
    color: '#1677ff',
    defaultName: t('hpModBannerBlockDefaultName'),
    defaultTitle: t('hpModBannerBlockDefaultTitle'),
  },
  {
    value: 'banner',
    label: t('hpModBannerLabel'),
    desc: t('hpModBannerDesc'),
    icon: <PictureOutlined />,
    color: '#18a058',
    defaultName: t('hpModBannerDefaultName'),
  },
  {
    value: 'shop_product_block',
    label: t('hpModProductBlockLabel'),
    desc: t('hpModProductBlockDesc'),
    icon: <ShoppingOutlined />,
    color: '#f59e0b',
    defaultName: t('hpModProductBlockDefaultName'),
    defaultTitle: t('hpModProductBlockDefaultTitle'),
  },
  {
    value: 'shop_product',
    label: t('hpModProductLabel'),
    desc: t('hpModProductDesc'),
    icon: <ShoppingOutlined />,
    color: '#f59e0b',
    defaultName: t('hpModProductDefaultName'),
  },
];

const LANGUAGES = [
  { key: 'zh', label: '中文', tag: 'ZH', color: 'red' },
  { key: 'en', label: 'English', tag: 'EN', color: 'blue' },
  { key: 'pl', label: 'Polski', tag: 'PL', color: 'gold' },
];

const isRoot = (item: HomepageConfigItem) => !item.pId || item.pId === 0;

const ROOT_MODULE_CATEGORIES = ['banner_block', 'shop_product_block'];
const CHILD_MODULE_CATEGORIES: Record<string, string[]> = {
  banner_block: ['banner'],
  shop_product_block: ['shop_product'],
};

const BANNER_JUMP_TYPES = [
  { value: 'webpage', label: '网页' },
  { value: 'wmp', label: '小程序内页面' },
  { value: 'server_url', label: '后台返回地址' },
  { value: 'shop_product_group', label: '商品组' },
];

const canAddChildren = (item: HomepageConfigItem) => Boolean(CHILD_MODULE_CATEGORIES[item.category]?.length);

const canMoveToRoot = (item: HomepageConfigItem) => !['banner', 'shop_product'].includes(item.category);

const isVideoUrl = (url?: string) => Boolean(url && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url));

const getProductGroupI18nValue = (group: ProductGroup | undefined, lang: string, field: 'name' | 'remark' | 'specialTips') => {
  if (!group) return '';
  const i18nValue = group.i18n?.[lang]?.[field];
  if (i18nValue) return i18nValue;
  if (lang === 'zh') return (group[field] as string | undefined) || '';
  return '';
};

const getProductGroupLogo = (group: ProductGroup | undefined) =>
  group?.i18n?.zh?.logoUrl || group?.logoUrl || group?.i18n?.pl?.logoUrl || group?.i18n?.en?.logoUrl || '';

const getProductGroupOptionDescription = (group: ProductGroup) =>
  LANGUAGES.map((lang) => {
    const name = getProductGroupI18nValue(group, lang.key, 'name') || '未配置名称';
    const tips = getProductGroupI18nValue(group, lang.key, 'specialTips') || '暂无特殊提示';
    return `${lang.tag}: ${name} / ${tips}`;
  }).join(' | ');

const normalizeItem = (item: any, parentId: number | null = null): HomepageConfigItem => ({
  id: item.id,
  pId: parentId ?? item.pId ?? 0,
  sdkProductId: item.sdkProductId || 10005,
  code: item.code || '',
  name: item.name || '',
  title: item.title || '',
  category: item.category || '',
  shopProductId: item.shopProductId,
  imageUrl: item.imageUrl,
  jumpUrl: item.jumpUrl,
  jumpType: item.jumpType,
  status: item.status ?? 1,
  seq: item.seq || 1,
  beginTime: item.beginTime || '',
  endTime: item.endTime,
  isExist: item.isExist ?? 1,
  gmtCreate: item.gmtCreate || '',
  gmtModified: item.gmtModified || '',
  remark: item.remark,
  children: item.children,
});

const flattenTreeData = (treeData: any[]): HomepageConfigItem[] => {
  const result: HomepageConfigItem[] = [];
  const walk = (items: any[], parentId: number | null = null) => {
    items.forEach((item) => {
      result.push(normalizeItem(item, parentId));
      if (Array.isArray(item.children) && item.children.length > 0) {
        walk(item.children, item.id);
      }
    });
  };
  walk(treeData);
  return result;
};

const buildChildrenMap = (items: HomepageConfigItem[]) => {
  const map = new Map<number, HomepageConfigItem[]>();
  items.forEach((item) => {
    const parentId = item.pId || 0;
    if (!map.has(parentId)) {
      map.set(parentId, []);
    }
    map.get(parentId)!.push(item);
  });
  map.forEach((children) => children.sort((a, b) => (a.seq || 0) - (b.seq || 0) || a.id - b.id));
  return map;
};

const HomepageConfig: React.FC = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState<HomepageConfigItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<HomepageConfigItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('visual');
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('desktop');
  const [dragOverKey, setDragOverKey] = useState<string>('');
  const [i18nImageUrls, setI18nImageUrls] = useState<Record<string, string>>({});
  const [i18nImageUploading, setI18nImageUploading] = useState<Record<string, boolean>>({});
  const [productGroupDetails, setProductGroupDetails] = useState<Record<string, ProductGroup>>({});
  const [productGroupLoadingIds, setProductGroupLoadingIds] = useState<Record<string, boolean>>({});
  const [productGroupOptions, setProductGroupOptions] = useState<ProductGroup[]>([]);
  const [productGroupOptionsLoading, setProductGroupOptionsLoading] = useState(false);
  const [form] = Form.useForm();
  const draggingPaletteCategoryRef = useRef('');
  const draggingItemCategoryRef = useRef('');

  const moduleTypes = useMemo(() => buildModuleTypes(t), [language, t]);
  const moduleMetaMap = useMemo(
    () =>
      moduleTypes.reduce<Record<string, ModuleMeta>>((acc, item) => {
        acc[item.value] = item;
        return acc;
      }, {}),
    [moduleTypes]
  );
  const getModuleMeta = useCallback(
    (category?: string): ModuleMeta =>
      moduleMetaMap[category || ''] || {
        value: category || 'unknown',
        label: t('hpModUnknownLabel'),
        desc: t('hpModUnknownDesc'),
        icon: <ApartmentOutlined />,
        color: '#64748b',
        defaultName: t('hpModUnknownDefaultName'),
      },
    [moduleMetaMap, t]
  );

  const childrenMap = useMemo(() => buildChildrenMap(configData), [configData]);
  const rootItems = childrenMap.get(0) || [];
  const selectedComponent = selectedId ? configData.find((item) => item.id === selectedId) || null : null;
  const productGroupIds = useMemo(
    () =>
      Array.from(
        new Set(
          configData
            .filter((item) => item.category === 'shop_product' && item.shopProductId)
            .map((item) => String(item.shopProductId))
        )
      ),
    [configData]
  );

  useEffect(() => {
    const root = document.querySelector('.homepage-lowcode-page');
    if (root) {
      translateAdminDom(language, root);
    }
  }, [language, configData, viewMode, modalVisible, selectedId, loading, productGroupOptions.length]);

  const fetchHomepageConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await homepageConfigService.getHomepageTree();
      const flatData = flattenTreeData(response?.data || []);
      setConfigData(flatData);
      setSelectedId((current) => (current && flatData.some((item) => item.id === current) ? current : flatData[0]?.id || null));
    } catch (error: any) {
      console.error('获取首页配置失败:', error);
      message.error(error?.message || '获取首页配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProductGroupOptions = useCallback(async (search = '') => {
    setProductGroupOptionsLoading(true);
    try {
      const response = await productGroupService.getProductGroupList({
        pageNo: 1,
        pageSize: 50,
        search,
      });
      setProductGroupOptions(response?.data?.records || []);
    } catch (error: any) {
      console.error('获取商品组列表失败:', error);
      message.error(error?.message || '获取商品组列表失败');
    } finally {
      setProductGroupOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHomepageConfig();
  }, [fetchHomepageConfig]);

  useEffect(() => {
    productGroupIds.forEach((groupId) => {
      if (productGroupDetails[groupId] || productGroupLoadingIds[groupId]) {
        return;
      }
      setProductGroupLoadingIds((prev) => ({ ...prev, [groupId]: true }));
      productGroupService
        .getProductGroupDetail(groupId)
        .then((response) => {
          const detail = response?.data;
          if (detail) {
            setProductGroupDetails((prev) => ({ ...prev, [groupId]: detail }));
          }
        })
        .catch((error: any) => {
          console.error('获取商品组详情失败:', error);
        })
        .finally(() => {
          setProductGroupLoadingIds((prev) => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
        });
    });
  }, [productGroupIds, productGroupDetails, productGroupLoadingIds]);

  const getSiblings = (parentId: number) => childrenMap.get(parentId) || [];

  const getNextSeq = (parentId: number) => getSiblings(parentId).length + 1;

  const isDescendantOf = (candidateId: number, ancestorId: number): boolean => {
    let current = configData.find((item) => item.id === candidateId);
    while (current?.pId) {
      if (current.pId === ancestorId) {
        return true;
      }
      const parentId = current.pId;
      current = configData.find((item) => item.id === parentId);
    }
    return false;
  };

  const persistSiblingOrder = async (parentId: number, items: HomepageConfigItem[]) => {
    await Promise.all(items.map((item, index) => homepageConfigService.updateSort(item.id, index + 1, parentId)));
  };

  const handleI18nImageUpload = async (file: File, lang: string) => {
    setI18nImageUploading((prev) => ({ ...prev, [lang]: true }));
    try {
      const response = await commonAttachFileService.uploadUrl(file);
      const uploadedUrl = response.data;
      setI18nImageUrls((prev) => ({ ...prev, [lang]: uploadedUrl }));
      if (lang === 'default') {
        form.setFieldValue('imageUrl', uploadedUrl);
      } else {
        form.setFieldValue(['i18n', lang, 'imageUrl'], uploadedUrl);
      }
      message.success('图片上传成功');
    } catch (error: any) {
      console.error('图片上传失败:', error);
      message.error(error?.message || '图片上传失败');
    } finally {
      setI18nImageUploading((prev) => ({ ...prev, [lang]: false }));
    }
    return false;
  };

  const handleRemoveI18nImage = (lang: string) => {
    setI18nImageUrls((prev) => {
      const next = { ...prev };
      delete next[lang];
      return next;
    });
    if (lang === 'default') {
      form.setFieldValue('imageUrl', '');
    } else {
      form.setFieldValue(['i18n', lang, 'imageUrl'], '');
    }
  };

  const handleProductGroupSelect = async (groupId: string) => {
    const cached = productGroupDetails[groupId] || productGroupOptions.find((option) => String(option.id) === groupId);
    if (cached) {
      setProductGroupDetails((prev) => ({ ...prev, [groupId]: cached }));
      form.setFieldsValue({
        shopProductId: groupId,
        name: cached.name || getModuleMeta('shop_product').defaultName,
        title: cached.name || '',
      });
      return;
    }

    setProductGroupLoadingIds((prev) => ({ ...prev, [groupId]: true }));
    try {
      const response = await productGroupService.getProductGroupDetail(groupId);
      const detail = response?.data;
      if (detail) {
        setProductGroupDetails((prev) => ({ ...prev, [groupId]: detail }));
        form.setFieldsValue({
          shopProductId: groupId,
          name: detail.name || getModuleMeta('shop_product').defaultName,
          title: detail.name || '',
        });
      }
    } catch (error: any) {
      console.error('获取商品组详情失败:', error);
      message.error(error?.message || '获取商品组详情失败');
    } finally {
      setProductGroupLoadingIds((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    }
  };

  const createComponent = async (category: string, parentId = 0) => {
    const meta = getModuleMeta(category);
    if (!ROOT_MODULE_CATEGORIES.includes(category)) {
      const parent = configData.find((item) => item.id === parentId);
      if (!parent || !CHILD_MODULE_CATEGORIES[parent.category]?.includes(category)) {
        message.warning(`${meta.label}需要拖入对应父模块中`);
        return;
      }
    }
    const timestamp = Date.now();
    const data: any = {
      sdkProductId: 10005,
      code: `${category}_${timestamp}`,
      name: meta.defaultName,
      title: meta.defaultTitle || '',
      category,
      status: 1,
      isExist: 1,
      seq: getNextSeq(parentId),
    };
    if (parentId > 0) {
      data.pId = parentId;
    }

    try {
      const response = await homepageConfigService.saveConfig(data);
      message.success('组件添加成功');
      await fetchHomepageConfig();
      const createdId = response?.data?.id;
      if (createdId) {
        setSelectedId(createdId);
      }
    } catch (error: any) {
      console.error('创建组件失败:', error);
      message.error(error?.message || '创建组件失败');
    }
  };

  const handleEdit = async (item: HomepageConfigItem) => {
    try {
      const response = await homepageConfigService.getConfigDetail(item.id);
      const detail = {
        ...item,
        ...(response.data || {}),
        pId: response.data?.pId ?? item.pId,
      };
      const nextI18nImages: Record<string, string> = {};
      if (detail.imageUrl) {
        nextI18nImages.default = detail.imageUrl;
      }
      if (detail.i18n) {
        Object.keys(detail.i18n).forEach((lang) => {
          if (detail.i18n[lang]?.imageUrl) {
            nextI18nImages[lang] = detail.i18n[lang].imageUrl;
          }
        });
      }
      setI18nImageUrls(nextI18nImages);
      setEditingItem(detail);
      setModalVisible(true);
      if (detail.category === 'shop_product' || detail.category === 'banner') {
        fetchProductGroupOptions();
      }
      form.setFieldsValue({
        ...detail,
        parentId: detail.pId || undefined,
        i18n: detail.i18n,
      });
    } catch (error: any) {
      console.error('获取详情失败:', error);
      message.error(error?.message || '获取详情失败');
    }
  };

  const handleSave = async (values: any) => {
    try {
      const parentId = values.parentId ?? editingItem?.pId;
      const data: any = {
        ...values,
        code: editingItem?.code || `${values.category}_${Date.now()}`,
        status: values.status ?? 1,
        isExist: 1,
        seq: editingItem?.seq || getNextSeq(parentId || 0),
      };
      if (parentId && parentId > 0) {
        data.pId = parentId;
      }

      if (values.category === 'banner' && Object.keys(i18nImageUrls).length > 0) {
        data.i18n = LANGUAGES.reduce<Record<string, { imageUrl?: string }>>((acc, lang) => {
          const imageUrl = i18nImageUrls[lang.key] || values.i18n?.[lang.key]?.imageUrl;
          if (imageUrl) {
            acc[lang.key] = { imageUrl };
          }
          return acc;
        }, {});
        data.imageUrl = i18nImageUrls.default || values.imageUrl || i18nImageUrls.zh || i18nImageUrls.pl || i18nImageUrls.en;
      }

      if (values.category === 'banner') {
        if (values.jumpType === 'shop_product_group') {
          data.jumpUrl = '';
        } else {
          data.shopProductId = '';
        }
      }

      if (values.category === 'shop_product' && values.shopProductId) {
        const groupId = String(values.shopProductId);
        const group = productGroupDetails[groupId] || productGroupOptions.find((option) => String(option.id) === groupId);
        data.title = group?.name || data.title;
        data.name = group?.name || data.name || getModuleMeta(values.category).defaultName;
      }

      if (editingItem) {
        await homepageConfigService.updateConfig({ ...data, id: editingItem.id });
        message.success('更新成功');
      } else {
        await homepageConfigService.saveConfig(data);
        message.success('新增成功');
      }

      setModalVisible(false);
      setEditingItem(null);
      setI18nImageUrls({});
      form.resetFields();
      await fetchHomepageConfig();
    } catch (error: any) {
      console.error('保存失败:', error);
      message.error(error?.message || '保存失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await homepageConfigService.operationConfig(id, 'delete');
      message.success('删除成功');
      if (selectedId === id) {
        setSelectedId(null);
      }
      await fetchHomepageConfig();
    } catch (error: any) {
      console.error('删除失败:', error);
      message.error(error?.message || '删除失败');
    }
  };

  const moveToParent = async (item: HomepageConfigItem, parentId: number) => {
    if (item.id === parentId) {
      return;
    }
    if (parentId === 0 && !canMoveToRoot(item)) {
      message.warning(`${getModuleMeta(item.category).label}需要放在对应模块内`);
      return;
    }
    try {
      await homepageConfigService.updateSort(item.id, getNextSeq(parentId), parentId);
      await fetchHomepageConfig();
      setSelectedId(item.id);
      message.success(parentId === 0 ? '已移到根级' : '已设为子组件');
    } catch (error: any) {
      console.error('移动失败:', error);
      message.error(error?.message || '移动失败');
    }
  };

  const isCategoryAllowedInParent = (category: string, parent: HomepageConfigItem) =>
    CHILD_MODULE_CATEGORIES[parent.category]?.includes(category);

  const getDraggingItem = (event: React.DragEvent) => {
    const draggedId = Number(event.dataTransfer.getData('homepage-item-id') || event.dataTransfer.getData('text/plain'));
    if (!draggedId) {
      return null;
    }
    return configData.find((item) => item.id === draggedId) || null;
  };

  const canDropIntoParent = (event: React.DragEvent, parent: HomepageConfigItem) => {
    if (!canAddChildren(parent)) {
      return false;
    }

    const category = event.dataTransfer.getData('homepage-module-category');
    if (category) {
      return isCategoryAllowedInParent(category, parent);
    }

    if (event.dataTransfer.types.includes('homepage-module-category')) {
      return true;
    }

    if (!event.dataTransfer.types.includes('homepage-item-id') && !event.dataTransfer.types.includes('text/plain')) {
      return false;
    }

    const dragged = getDraggingItem(event);
    if (!dragged) {
      return true;
    }
    return Boolean(dragged && dragged.id !== parent.id && !isDescendantOf(parent.id, dragged.id) && isCategoryAllowedInParent(dragged.category, parent));
  };

  const handleDropIntoParent = async (event: React.DragEvent, parent: HomepageConfigItem) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverKey('');

    const category = event.dataTransfer.getData('homepage-module-category');
    if (category) {
      if (!isCategoryAllowedInParent(category, parent)) {
        message.warning(`${getModuleMeta(category).label}不能放入${getModuleMeta(parent.category).label}`);
        return;
      }
      await createComponent(category, parent.id);
      return;
    }

    const dragged = getDraggingItem(event);
    if (!dragged) {
      return;
    }
    if (dragged.id === parent.id) {
      return;
    }
    if (isDescendantOf(parent.id, dragged.id)) {
      message.warning('不能移动到自己的子组件内');
      return;
    }
    if (!isCategoryAllowedInParent(dragged.category, parent)) {
      message.warning(`${getModuleMeta(dragged.category).label}不能放入${getModuleMeta(parent.category).label}`);
      return;
    }

    await moveToParent(dragged, parent.id);
  };

  const handleDragStart = (event: React.DragEvent, item: HomepageConfigItem) => {
    event.stopPropagation();
    event.dataTransfer.clearData();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('homepage-item-id', item.id.toString());
    event.dataTransfer.setData('text/plain', item.id.toString());
    draggingItemCategoryRef.current = item.category;
    draggingPaletteCategoryRef.current = '';
  };

  const handlePaletteDragStart = (event: React.DragEvent, category: string) => {
    event.stopPropagation();
    event.dataTransfer.clearData();
    event.dataTransfer.effectAllowed = 'copyMove';
    event.dataTransfer.setData('homepage-module-category', category);
    event.dataTransfer.setData('text/plain', `homepage-module:${category}`);
    draggingPaletteCategoryRef.current = category;
    draggingItemCategoryRef.current = '';
  };

  const handleDragEnd = () => {
    draggingPaletteCategoryRef.current = '';
    draggingItemCategoryRef.current = '';
    setDragOverKey('');
  };

  const handleToggleStatus = async (item: HomepageConfigItem) => {
    const nextStatus = item.status === 1 ? 0 : 1;
    const operation = nextStatus === 1 ? 'enable' : 'disable';
    try {
      await homepageConfigService.operationConfig(item.id, operation);
      message.success(nextStatus === 1 ? '已启用' : '已禁用');
      await fetchHomepageConfig();
      setSelectedId(item.id);
    } catch (error: any) {
      console.error('状态更新失败:', error);
      message.error(error?.message || '状态更新失败');
    }
  };

  const handleDrop = async (event: React.DragEvent, parentId: number, targetId: number | null, position: DropPosition) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverKey('');

    const category = event.dataTransfer.getData('homepage-module-category');
    if (category) {
      const nextParentId = position === 'inside' && targetId ? targetId : parentId;
      const parent = configData.find((item) => item.id === nextParentId);
      if (nextParentId > 0 && (!parent || !CHILD_MODULE_CATEGORIES[parent.category]?.includes(category))) {
        message.warning(`${getModuleMeta(category).label}不能放入${parent ? getModuleMeta(parent.category).label : '当前模块'}`);
        return;
      }
      if (nextParentId === 0 && !ROOT_MODULE_CATEGORIES.includes(category)) {
        message.warning(`${getModuleMeta(category).label}需要拖入对应父模块中`);
        return;
      }
      await createComponent(category, nextParentId);
      return;
    }

    const draggedId = Number(event.dataTransfer.getData('homepage-item-id') || event.dataTransfer.getData('text/plain'));
    if (!draggedId) {
      return;
    }
    const dragged = configData.find((item) => item.id === draggedId);
    if (!dragged || dragged.id === targetId) {
      return;
    }
    if (targetId && isDescendantOf(targetId, dragged.id)) {
      message.warning('不能移动到自己的子组件内');
      return;
    }

    if (position === 'inside' && targetId) {
      const target = configData.find((item) => item.id === targetId);
      if (!target || !CHILD_MODULE_CATEGORIES[target.category]?.includes(dragged.category)) {
        message.warning(`${getModuleMeta(dragged.category).label}不能放入${target ? getModuleMeta(target.category).label : '当前模块'}`);
        return;
      }
      await moveToParent(dragged, targetId);
      return;
    }

    if (parentId === 0 && !canMoveToRoot(dragged)) {
      message.warning(`${getModuleMeta(dragged.category).label}需要放在对应模块内`);
      return;
    }
    if (parentId > 0) {
      const parent = configData.find((item) => item.id === parentId);
      if (!parent || !CHILD_MODULE_CATEGORIES[parent.category]?.includes(dragged.category)) {
        message.warning(`${getModuleMeta(dragged.category).label}不能放入${parent ? getModuleMeta(parent.category).label : '当前模块'}`);
        return;
      }
    }

    const siblings = [...getSiblings(parentId)].filter((item) => item.id !== draggedId);
    const targetIndex = targetId ? siblings.findIndex((item) => item.id === targetId) : siblings.length;
    const insertIndex = position === 'before' ? Math.max(targetIndex, 0) : Math.max(targetIndex + 1, 0);
    siblings.splice(insertIndex, 0, { ...dragged, pId: parentId });
    try {
      await persistSiblingOrder(parentId, siblings);
      await fetchHomepageConfig();
      setSelectedId(dragged.id);
      message.success('位置已更新');
    } catch (error: any) {
      console.error('拖拽排序失败:', error);
      message.error(error?.message || '拖拽排序失败');
    }
  };

  const canDropOnSortZone = (event: React.DragEvent, parentId: number) => {
    const category = event.dataTransfer.getData('homepage-module-category') || draggingPaletteCategoryRef.current;
    if (category) {
      return parentId > 0 || ROOT_MODULE_CATEGORIES.includes(category);
    }
    if (event.dataTransfer.types.includes('homepage-module-category')) {
      return parentId > 0;
    }
    const draggedCategory = draggingItemCategoryRef.current;
    if (draggedCategory && parentId === 0 && !canMoveToRoot({ category: draggedCategory } as HomepageConfigItem)) {
      return false;
    }
    return true;
  };

  const renderDropZone = (key: string, parentId: number, targetId: number | null, position: DropPosition) => (
    <div
      className={`homepage-drop-zone ${dragOverKey === key ? 'is-active' : ''}`}
      onDragOver={(event) => {
        if (!canDropOnSortZone(event, parentId)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = event.dataTransfer.types.includes('homepage-module-category') ? 'copy' : 'move';
        setDragOverKey(key);
      }}
      onDragLeave={(event) => {
        event.stopPropagation();
        setDragOverKey('');
      }}
      onDrop={(event) => handleDrop(event, parentId, targetId, position)}
    >
      <span>{position === 'inside' ? '拖到这里成为子组件' : '拖到这里调整顺序'}</span>
    </div>
  );

  const renderChildDropZone = (key: string, parentId: number, targetId: number | null, position: DropPosition) => (
    <div
      className={`homepage-child-thumb-drop-zone ${dragOverKey === key ? 'is-active' : ''}`}
      onDragOver={(event) => {
        if (!canDropOnSortZone(event, parentId)) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = event.dataTransfer.types.includes('homepage-module-category') ? 'copy' : 'move';
        setDragOverKey(key);
      }}
      onDragLeave={(event) => {
        event.stopPropagation();
        setDragOverKey('');
      }}
      onDrop={(event) => handleDrop(event, parentId, targetId, position)}
    />
  );

  const handleGalleryDragOver = (event: React.DragEvent, parent: HomepageConfigItem) => {
    if (!canDropIntoParent(event, parent)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = event.dataTransfer.types.includes('homepage-module-category') ? 'copy' : 'move';
    setDragOverKey(`${parent.id}-gallery`);
  };

  const handleGalleryDrop = (event: React.DragEvent, parent: HomepageConfigItem) => {
    handleDropIntoParent(event, parent);
  };

  const getItemImageUrl = (item: HomepageConfigItem) => {
    if (item.category === 'shop_product' && item.shopProductId) {
      return getProductGroupLogo(productGroupDetails[String(item.shopProductId)]);
    }
    return item.imageUrl || (item as any).logoUrl || '';
  };

  const renderProductGroupTags = (group: ProductGroup | undefined) => (
    <div className="homepage-product-group-tags">
      {LANGUAGES.map((lang) => {
        const tips = getProductGroupI18nValue(group, lang.key, 'specialTips');
        return (
          <Tag key={lang.key} color={lang.color}>
            {lang.tag} {tips || '暂无特殊提示'}
          </Tag>
        );
      })}
    </div>
  );

  const renderProductGroupCard = (item: HomepageConfigItem, compact = false) => {
    const groupId = item.shopProductId ? String(item.shopProductId) : '';
    const group = groupId ? productGroupDetails[groupId] : undefined;
    const logoUrl = getProductGroupLogo(group);
    const loadingGroup = groupId ? productGroupLoadingIds[groupId] : false;
    const title = group?.name || item.title || item.name || '未配置商品组';
    const cardContent = (
      <>
        <span className="homepage-product-group-media">
          {logoUrl ? <img src={logoUrl} alt={title} /> : <ShoppingOutlined />}
        </span>
        <span className="homepage-product-group-body">
          <span className="homepage-product-group-title">{title}</span>
          {loadingGroup ? (
            <span className="homepage-product-group-loading">商品组详情加载中...</span>
          ) : (
            renderProductGroupTags(group)
          )}
          {!compact && groupId && <span className="homepage-product-group-hint">点击查看商品组详情</span>}
        </span>
      </>
    );

    if (!compact && groupId) {
      return (
        <button
          type="button"
          className="homepage-product-group-link"
          onClick={(event) => {
            event.stopPropagation();
            window.open(`/product-group/detail/${groupId}`, '_blank', 'noopener,noreferrer');
          }}
        >
          <span className="homepage-product-group-card">{cardContent}</span>
        </button>
      );
    }

    return (
      <span className={`homepage-product-group-card ${compact ? 'is-compact' : ''}`}>
        {cardContent}
      </span>
    );
  };

  const renderChildThumbnail = (child: HomepageConfigItem, index: number, parentId: number) => {
    const meta = getModuleMeta(child.category);
    const imageUrl = getItemImageUrl(child);
    const isSelected = selectedId === child.id;
    const isVideo = isVideoUrl(imageUrl);

    return (
      <React.Fragment key={child.id}>
        {renderChildDropZone(`${parentId}-${child.id}-thumb-before`, parentId, child.id, 'before')}
        <button
          type="button"
          className={`homepage-child-thumb ${isSelected ? 'is-selected' : ''}`}
          draggable
          onDragStart={(event) => handleDragStart(event, child)}
          onDragEnd={handleDragEnd}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedId(child.id);
          }}
        >
          <span className="homepage-child-actions" onClick={(event) => event.stopPropagation()}>
            <Tooltip title={child.status === 1 ? t('actionDisable') : t('actionEnable')}>
              <Button
                size="small"
                icon={<PoweroffOutlined />}
                type={child.status === 1 ? 'default' : 'primary'}
                danger={child.status === 1}
                onClick={() => handleToggleStatus(child)}
              />
            </Tooltip>
            <Popconfirm
              title="确认删除"
              description="删除后该模块不再在首页展示，确认继续？"
              onConfirm={() => handleDelete(child.id)}
              okText="确认"
              cancelText="取消"
            >
              <Tooltip title="删除">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </span>
          {child.category === 'shop_product' ? (
            renderProductGroupCard(child, true)
          ) : (
            <>
              <span className="homepage-child-thumb-media">
                {isVideo ? (
                  <>
                    <video src={imageUrl} muted preload="metadata" />
                    <PlayCircleOutlined className="homepage-child-video-icon" />
                  </>
                ) : imageUrl ? (
                  <img src={imageUrl} alt={child.name || meta.label} />
                ) : (
                  meta.icon
                )}
              </span>
              <span className="homepage-child-thumb-title">{child.title || child.name || `${meta.label} ${index + 1}`}</span>
              <span className="homepage-child-thumb-meta">{meta.label}</span>
            </>
          )}
          <span className={`homepage-child-status ${child.status === 1 ? 'is-enabled' : 'is-disabled'}`}>
            {child.status === 1 ? t('actionEnable') : t('actionDisable')}
          </span>
        </button>
      </React.Fragment>
    );
  };

  const renderChildrenGallery = (parent: HomepageConfigItem, children: HomepageConfigItem[], type: 'banner' | 'product') => {
    const emptyText = type === 'banner' ? '拖入 Banner 图' : '拖入商品组信息';
    return (
      <div
        className={`homepage-child-gallery is-${type} ${dragOverKey === `${parent.id}-gallery` ? 'is-drop-active' : ''}`}
        onDragOver={(event) => handleGalleryDragOver(event, parent)}
        onDragLeave={(event) => {
          event.stopPropagation();
          setDragOverKey('');
        }}
        onDrop={(event) => handleGalleryDrop(event, parent)}
      >
        <div className="homepage-child-gallery-head">
          <strong>{type === 'banner' ? 'Banner轮播区域' : parent.title || '商品组展示区域'}</strong>
          <span>{children.length} {type === 'banner' ? '张 Banner' : '个商品组'}</span>
        </div>
        <div className="homepage-child-gallery-list">
          {children.length ? (
            <>
              {children.map((child, index) => renderChildThumbnail(child, index, parent.id))}
              {renderChildDropZone(`${parent.id}-thumb-end`, parent.id, children[children.length - 1].id, 'after')}
            </>
          ) : (
            <>
              {renderChildDropZone(`${parent.id}-thumb-empty`, parent.id, null, 'before')}
              <button
                type="button"
                className="homepage-child-empty"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedId(parent.id);
                }}
              >
                <PlusOutlined />
                <span>{emptyText}</span>
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderComponentPreview = (item: HomepageConfigItem) => {
    const meta = getModuleMeta(item.category);
    const children = childrenMap.get(item.id) || [];

    if (item.category === 'banner_block') {
      return renderChildrenGallery(item, children, 'banner');
    }

    if (item.category === 'banner') {
      const isVideo = isVideoUrl(item.imageUrl);
      return (
        <div className="homepage-preview-banner">
          {isVideo ? (
            <>
              <video src={item.imageUrl} muted preload="metadata" />
              <PlayCircleOutlined className="homepage-preview-video-icon" />
            </>
          ) : item.imageUrl ? (
            <img src={item.imageUrl} alt={item.name} />
          ) : (
            <PictureOutlined />
          )}
          <span>{item.title || item.name || 'Banner图'}</span>
        </div>
      );
    }

    if (item.category === 'shop_product_block') {
      return renderChildrenGallery(item, children, 'product');
    }

    if (item.category === 'shop_product') {
      return renderProductGroupCard(item);
    }

    return <div className="homepage-preview-text">{meta.label}</div>;
  };

  const renderNode = (item: HomepageConfigItem, level = 0): React.ReactNode => {
    const meta = getModuleMeta(item.category);
    const children = childrenMap.get(item.id) || [];
    const parentId = item.pId || 0;
    const selected = selectedId === item.id;

    return (
      <div key={item.id} className="homepage-node-wrap" style={{ marginLeft: level * 18 }}>
        {renderDropZone(`${parentId}-${item.id}-before`, parentId, item.id, 'before')}
        <div
          className={`homepage-node ${selected ? 'is-selected' : ''}`}
          draggable
          onDragStart={(event) => handleDragStart(event, item)}
          onDragEnd={handleDragEnd}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedId(item.id);
          }}
        >
          <div className="homepage-node-top">
            <div className="homepage-node-icon" style={{ color: meta.color, backgroundColor: `${meta.color}14` }}>
              {meta.icon}
            </div>
            <div className="homepage-node-title">
              <strong>{item.name || meta.label}</strong>
              <span>{meta.label} · ID {item.id}</span>
            </div>
            <div className="homepage-node-tags">
              <Tag color={item.status === 1 ? 'success' : 'default'}>{item.status === 1 ? t('actionEnable') : t('actionDisable')}</Tag>
            </div>
            <Space size={4} className="homepage-node-actions" onClick={(event) => event.stopPropagation()}>
              <Tooltip title={item.status === 1 ? t('actionDisable') : t('actionEnable')}>
                <Button
                  size="small"
                  icon={<PoweroffOutlined />}
                  type={item.status === 1 ? 'default' : 'primary'}
                  danger={item.status === 1}
                  onClick={() => handleToggleStatus(item)}
                />
              </Tooltip>
              <Popconfirm
                title="确认删除"
                description="删除后该模块不再在首页展示，确认继续？"
                onConfirm={() => handleDelete(item.id)}
                okText="确认"
                cancelText="取消"
              >
                <Tooltip title="删除">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          </div>
          <div className="homepage-node-preview">{renderComponentPreview(item)}</div>
          {item.category !== 'banner_block' && item.category !== 'shop_product_block' && children.length > 0 && (
            <div className="homepage-node-children">
              {children.map((child) => renderNode(child, level + 1))}
            </div>
          )}
        </div>
        {renderDropZone(`${parentId}-${item.id}-after`, parentId, item.id, 'after')}
      </div>
    );
  };

  const renderPalette = () => (
    <aside className="homepage-builder-palette" data-i18n-ignore="true">
        <div className="homepage-panel-head">
          <strong>{t('hpComponentLib')}</strong>
          <span>{t('hpPaletteHint')}</span>
        </div>
      <div className="homepage-palette-list">
        {moduleTypes.map((type) => (
          <button
            key={type.value}
            className="homepage-palette-item"
            draggable
            onDragStart={(event) => handlePaletteDragStart(event, type.value)}
            onDragEnd={handleDragEnd}
            onClick={() => message.info(t('hpDragToModuleHint'))}
            type="button"
          >
            <span className="homepage-palette-icon" style={{ color: type.color, backgroundColor: `${type.color}14` }}>
              {type.icon}
            </span>
            <span>
              <strong>{type.label}</strong>
              <small>{type.desc}</small>
            </span>
          </button>
        ))}
      </div>
    </aside>
  );

  const renderCanvas = () => (
    <main className="homepage-builder-canvas">
      <div className="homepage-canvas-toolbar">
        <div>
          <strong>{t('hpCanvasTitle')}</strong>
          <span>{t('hpCanvasHint')}</span>
        </div>
        <Segmented
          size="small"
          value={canvasMode}
          onChange={(value) => setCanvasMode(value as CanvasMode)}
          options={[
            { label: t('hpDesktop'), value: 'desktop' },
            { label: t('hpMobile'), value: 'mobile' },
          ]}
        />
      </div>
      <div className={`homepage-canvas-frame is-${canvasMode}`}>
        <div className="homepage-phone-top">
          <HomeOutlined />
          <span>Edison shop</span>
        </div>
        <div className="homepage-canvas-content">
          {renderDropZone('root-start', 0, rootItems[0]?.id || null, 'before')}
          {loading ? (
            <div className="homepage-empty-state">
              <Spin />
            </div>
          ) : rootItems.length ? (
            rootItems.map((item) => renderNode(item))
          ) : (
            <div
              className={`homepage-empty-state homepage-root-drop-target ${dragOverKey === 'root-empty' ? 'is-active' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = event.dataTransfer.types.includes('homepage-module-category') ? 'copy' : 'move';
                setDragOverKey('root-empty');
              }}
              onDragLeave={(event) => {
                event.stopPropagation();
                setDragOverKey('');
              }}
              onDrop={(event) => handleDrop(event, 0, null, 'before')}
            >
              <Empty description={t('hpEmptyModules')} />
            </div>
          )}
        </div>
      </div>
    </main>
  );

  const renderPropertyPanel = () => {
    const item = selectedComponent;
    if (!item) {
      return (
        <aside className="homepage-builder-props">
          <div className="homepage-panel-head">
            <strong>{t('hpPropsPanel')}</strong>
            <span>{t('hpPropsSelectHint')}</span>
          </div>
          <div className="homepage-props-empty">
            <ApartmentOutlined />
            <p>{t('hpPropsClickHint')}</p>
          </div>
        </aside>
      );
    }

    const meta = getModuleMeta(item.category);
    const parent = configData.find((candidate) => candidate.id === item.pId);
    const children = childrenMap.get(item.id) || [];

    return (
      <aside className="homepage-builder-props" data-i18n-ignore="true">
        <div className="homepage-panel-head">
          <strong>{t('hpPropsPanel')}</strong>
          <span>{t('hpPropsSelected', { module: meta.label })}</span>
        </div>
        <div className="homepage-props-card">
          <div className="homepage-props-title">
            <span className="homepage-node-icon" style={{ color: meta.color, backgroundColor: `${meta.color}14` }}>
              {meta.icon}
            </span>
            <div>
              <strong>{item.name || meta.label}</strong>
              <small>{item.category}</small>
            </div>
          </div>
          <dl className="homepage-props-list">
            <dt>ID</dt>
            <dd>{item.id}</dd>
            <dt>{t('hpParent')}</dt>
            <dd>{parent?.name || (isRoot(item) ? t('hpRoot') : item.pId)}</dd>
            <dt>{t('hpModuleTitle')}</dt>
            <dd>{item.title || t('emptyNone')}</dd>
            <dt>{t('fieldStatus')}</dt>
            <dd>
              <Tag color={item.status === 1 ? 'success' : 'default'}>{item.status === 1 ? t('actionEnable') : t('actionDisable')}</Tag>
            </dd>
            <dt>{t('hpChildComponents')}</dt>
            <dd>{children.length}</dd>
          </dl>
          <Space wrap>
            <Button type="primary" onClick={() => handleEdit(item)}>
              {t('hpEditProps')}
            </Button>
            <Button icon={<PoweroffOutlined />} danger={item.status === 1} onClick={() => handleToggleStatus(item)}>
              {item.status === 1 ? t('actionDisable') : t('actionEnable')}
            </Button>
          </Space>
        </div>
        <div className="homepage-props-card">
          <strong>{t('hpFrontendImpact')}</strong>
          <p>{meta.desc}</p>
          {item.category === 'banner' && <p>{t('hpBannerI18nTip')}</p>}
          {item.category === 'shop_product' && <p>{t('hpProductGroupClickTip')}</p>}
          {item.category === 'shop_product_block' && <p>{t('hpProductBlockChildTip')}</p>}
        </div>
        {item.category === 'shop_product' && item.shopProductId && (
          <div className="homepage-props-card">
            <strong>{t('hpProductGroupPreview')}</strong>
            <p>{t('hpProductGroupPreviewHint')}</p>
            <div className="homepage-props-product-group">
              {renderProductGroupCard(item)}
            </div>
          </div>
        )}
      </aside>
    );
  };

  const renderTree = () => {
    const toTreeData = (items: HomepageConfigItem[]): any[] =>
      items.map((item) => {
        const meta = getModuleMeta(item.category);
        return {
          key: item.id,
          title: (
            <div className="homepage-tree-node">
              <span style={{ color: meta.color }}>{meta.icon}</span>
              <strong>{item.name || meta.label}</strong>
              <Tag>{meta.label}</Tag>
              <Tag color={item.status === 1 ? 'success' : 'default'}>{item.status === 1 ? t('actionEnable') : t('actionDisable')}</Tag>
              {item.title && <span>{item.title}</span>}
            </div>
          ),
          children: toTreeData(childrenMap.get(item.id) || []),
        };
      });

    return (
      <Card className="homepage-tree-card">
        {rootItems.length ? (
          <Tabs
            items={[
              {
                key: 'tree',
                label: '配置树',
                children: (
                  <div className="homepage-tree-panel">
                    <Tree
                      showLine
                      defaultExpandAll
                      treeData={toTreeData(rootItems)}
                      onSelect={(keys) => setSelectedId(Number(keys[0]))}
                    />
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <Empty description="暂无配置数据" />
        )}
      </Card>
    );
  };

  const selectedParentOptions = configData.filter((item) => canAddChildren(item) && (!editingItem || item.id !== editingItem.id));
  const editingCategory = editingItem?.category || form.getFieldValue('category');
  const hideStructureFields = editingCategory === 'banner';

  return (
    <div className="homepage-lowcode-page">
      <div className="homepage-lowcode-header">
        <div>
          <h1>{t('hpTitle')}</h1>
          <p>{t('hpSubtitle')}</p>
        </div>
        <Space wrap>
          <Badge count={configData.length} overflowCount={999} color="#1677ff">
            <Button icon={<ReloadOutlined />} onClick={fetchHomepageConfig} loading={loading}>
              {t('actionRefresh')}
            </Button>
          </Badge>
        </Space>
      </div>

      <div className="homepage-lowcode-tabs">
        <Segmented
          value={viewMode}
          onChange={(value) => setViewMode(value as ViewMode)}
          options={[
            { label: t('hpVisualCanvas'), value: 'visual' },
            { label: t('hpTreeView'), value: 'tree' },
          ]}
        />
      </div>

      {viewMode === 'visual' ? (
        <div className="homepage-builder">
          {renderPalette()}
          {renderCanvas()}
          {renderPropertyPanel()}
        </div>
      ) : (
        renderTree()
      )}

      <Modal
        title={editingItem ? '编辑模块' : '新增模块'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingItem(null);
          setI18nImageUrls({});
          form.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          {!hideStructureFields && (
            <div className="homepage-form-grid">
              <Form.Item name="parentId" label="父级节点">
                <Select placeholder="不选则为根节点" allowClear>
                  <Option value={0}>根级</Option>
                  {selectedParentOptions.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name || getModuleMeta(item.category).label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="category" label="模块类型" rules={[{ required: true, message: '请选择模块类型' }]}>
                <Select placeholder="请选择模块类型" disabled={!!editingItem}>
                  {moduleTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}

          <div className="homepage-form-grid">
            <Form.Item name="name" label="模块名称" rules={[{ required: true, message: '请输入模块名称' }]}>
              <Input placeholder="例如：首页顶部Banner" />
            </Form.Item>
            <Form.Item name="title" label="模块标题">
              <Input placeholder="用户侧可见标题，可选" />
            </Form.Item>
          </div>

	          <Form.Item noStyle shouldUpdate={(prev, current) => prev.category !== current.category}>
            {() => {
              const category = form.getFieldValue('category');
              return (
                <>
                  {category === 'shop_product' && (
                    <>
                      <Form.Item name="shopProductId" label="选择商品组" rules={[{ required: true, message: '请选择商品组' }]}>
                        <Select
                          showSearch
                          placeholder="搜索并选择商品组"
                          loading={productGroupOptionsLoading}
                          filterOption={false}
                          onSearch={fetchProductGroupOptions}
                          onFocus={() => fetchProductGroupOptions()}
                          onChange={(value) => handleProductGroupSelect(String(value))}
                          optionLabelProp="label"
                        >
                          {productGroupOptions.map((group) => (
                            <Option key={group.id} value={String(group.id)} label={group.name}>
                              <div className="homepage-product-group-option">
                                {getProductGroupLogo(group) ? <img src={getProductGroupLogo(group)} alt={group.name} /> : <ShoppingOutlined />}
                                <span>
	                                  <strong>{group.name}</strong>
                                  <small>{getProductGroupOptionDescription(group)}</small>
                                </span>
                              </div>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, current) => prev.shopProductId !== current.shopProductId}>
                        {() => {
                          const groupId = form.getFieldValue('shopProductId');
                          if (!groupId) return null;
                          return (
                            <div className="homepage-form-product-preview">
                              {renderProductGroupCard({
                                ...(editingItem || ({} as HomepageConfigItem)),
                                id: editingItem?.id || 0,
                                shopProductId: String(groupId),
                                category: 'shop_product',
                                name: form.getFieldValue('name') || '商品组信息',
                                title: form.getFieldValue('title') || '',
                              } as HomepageConfigItem)}
                            </div>
                          );
                        }}
                      </Form.Item>
                    </>
                  )}
                  {category === 'banner' && (
                    <>
                      <Form.Item name="imageUrl" label="Banner图片/视频">
                        <div className="homepage-upload-field">
                          {i18nImageUrls.default && (
                            <div className="homepage-upload-preview is-large">
                              {isVideoUrl(i18nImageUrls.default) ? (
                                <video src={i18nImageUrls.default} controls muted preload="metadata" />
                              ) : (
                                <Image width={260} height={146} src={i18nImageUrls.default} alt="Banner媒体" />
                              )}
                              <Button danger size="small" onClick={() => handleRemoveI18nImage('default')}>
                                删除
                              </Button>
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nImageUpload(file, 'default')}
                            showUploadList={false}
                            disabled={i18nImageUploading.default}
                            accept="image/*,video/*"
                          >
                            <Button icon={<UploadOutlined />} loading={i18nImageUploading.default}>
                              {i18nImageUrls.default ? '重新上传' : '上传图片/视频'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                      <div className="homepage-form-grid">
                        <Form.Item name="jumpType" label="跳转类型">
                          <Select
                            allowClear
                            placeholder="请选择"
                            onChange={(value) => {
                              if (value === 'shop_product_group') {
                                form.setFieldValue('jumpUrl', '');
                                fetchProductGroupOptions();
                              } else {
                                form.setFieldValue('shopProductId', undefined);
                              }
                            }}
                          >
                            {BANNER_JUMP_TYPES.map((type) => (
                              <Option key={type.value} value={type.value}>
                                {type.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, current) => prev.jumpType !== current.jumpType}>
                          {() =>
                            form.getFieldValue('jumpType') === 'shop_product_group' ? (
                              <Form.Item name="shopProductId" label="选择商品组" rules={[{ required: true, message: '请选择商品组' }]}>
                                <Select
                                  showSearch
                                  placeholder="搜索并选择商品组"
                                  loading={productGroupOptionsLoading}
                                  filterOption={false}
                                  onSearch={fetchProductGroupOptions}
                                  onFocus={() => fetchProductGroupOptions()}
                                  onChange={(value) => handleProductGroupSelect(String(value))}
                                  optionLabelProp="label"
                                >
                                  {productGroupOptions.map((group) => (
                                    <Option key={group.id} value={String(group.id)} label={group.name}>
                                      <div className="homepage-product-group-option">
                                        {getProductGroupLogo(group) ? <img src={getProductGroupLogo(group)} alt={group.name} /> : <ShoppingOutlined />}
                                        <span>
                                          <strong>{group.name}</strong>
                                          <small>{getProductGroupOptionDescription(group)}</small>
                                        </span>
                                      </div>
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            ) : (
                              <Form.Item name="jumpUrl" label="跳转URL">
                                <Input placeholder="点击图片后的跳转地址，可选" />
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </div>
                    </>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="仅后台可见，记录运营说明" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, current) => prev.category !== current.category}>
            {() =>
              form.getFieldValue('category') === 'banner' && (
                <Form.Item label="Banner图片（多语言）">
                  <Tabs
                    items={LANGUAGES.map((lang) => ({
                      key: lang.key,
                      label: lang.label,
                      children: (
                        <Form.Item label={`图片URL（${lang.label}）`} name={['i18n', lang.key, 'imageUrl']}>
                          <div className="homepage-upload-field">
                            {i18nImageUrls[lang.key] && (
                              <div className="homepage-upload-preview">
                                {isVideoUrl(i18nImageUrls[lang.key]) ? (
                                  <video src={i18nImageUrls[lang.key]} controls muted preload="metadata" />
                                ) : (
                                  <Image width={220} height={124} src={i18nImageUrls[lang.key]} alt="Banner图片" />
                                )}
                                <Button danger size="small" onClick={() => handleRemoveI18nImage(lang.key)}>
                                  删除
                                </Button>
                              </div>
                            )}
                            <Upload
                              beforeUpload={(file) => handleI18nImageUpload(file, lang.key)}
                            showUploadList={false}
                            disabled={i18nImageUploading[lang.key]}
                            accept="image/*,video/*"
                          >
                            <Button icon={<UploadOutlined />} loading={i18nImageUploading[lang.key]}>
                              {i18nImageUrls[lang.key] ? '重新上传' : '上传图片/视频'}
                            </Button>
                          </Upload>
                          </div>
                        </Form.Item>
                      ),
                    }))}
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                保存
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingItem(null);
                  setI18nImageUrls({});
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HomepageConfig;
